import React, { useState, useEffect } from "react";
import { C, ORG, ROLE_NAMES, getDailyQuote, getPerms, hasRole, getPrimaryRole, authIdOf } from "./constants";
import { Card, KPICard, DataTable } from "./erp-shared";
import {
  getCommunityNewMuslims, getVolunteers, getEmployees, getMosques,
  getEvents, getWomenBeneficiaries, getAllUsers, getTransactions,
  getMosquePrograms, getWomenPrograms, getNotifications,
  getInstituteStudents, getInstituteCourses, getInstituteAttendance,
  getInstituteCertificates,
} from "./supabaseClient";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Area, AreaChart
} from "recharts";
import {
  LayoutDashboard, Users2, Globe, Landmark, Heart, CalendarDays,
  Wallet, HeartHandshake, Briefcase,
  HandHelping, UserCog,
  Clock, CheckCircle, FileText, DollarSign,
  BookOpen, Mic, MapPin, Star, Target, Activity,
  BarChart3, PieChart as PieChartIcon,
  Users, ClipboardList, GraduationCap, Award
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Dashboard Page
   ═════════════════════════════════════════════ */
export default function DashboardPage({ user, onNavigate }) {
  const quote = getDailyQuote();
  const perms = getPerms(user);

  // كل مصادر مؤشرات لوحة القيادة تُحمَّل حيّة من Supabase (لا أرقام يدوية).
  const [d, setD] = useState({
    newMuslims: [], volunteers: [], employees: [], mosques: [], events: [],
    womenBen: [], users: [], donorIncome: [], finIncome: [], finExpense: [],
    mosquePrograms: [], womenPrograms: [],
  });
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      getCommunityNewMuslims(), getVolunteers(), getEmployees(), getMosques(),
      getEvents(), getWomenBeneficiaries(), getAllUsers(),
      getTransactions("donors", "income"), getTransactions("finance", "income"),
      getTransactions("finance", "expense"), getMosquePrograms(), getWomenPrograms(),
    ]).then(([nm, vol, emp, mq, ev, wb, us, di, fi, fe, mp, wp]) => {
      if (!alive) return;
      const val = (r) => (r.status === "fulfilled" && Array.isArray(r.value) ? r.value : []);
      setD({
        newMuslims: val(nm), volunteers: val(vol), employees: val(emp), mosques: val(mq),
        events: val(ev), womenBen: val(wb), users: val(us), donorIncome: val(di),
        finIncome: val(fi), finExpense: val(fe), mosquePrograms: val(mp), womenPrograms: val(wp),
      });
    });
    return () => { alive = false; };
  }, []);

  if (hasRole(user, "preacher")) return <PreacherDashboard user={user} quote={quote} onNavigate={onNavigate} />;
  // مدير المعهد يرى لوحته المخصّصة ما لم يكن أيضًا تنفيذيًا/سكرتيرًا
  if (hasRole(user, "institute_manager") && !perms.adminOnly) {
    return <InstituteDashboard user={user} quote={quote} />;
  }
  const isAdmin = perms.adminOnly;

  // ── مؤشرات مشتقّة من البيانات الحيّة ──
  const sumAmount = (rows) => rows.reduce((t, r) => t + (Number(r.amount) || 0), 0);
  const totalRevenue = sumAmount(d.finIncome) + sumAmount(d.donorIncome);
  const totalExpense = sumAmount(d.finExpense);
  const activePrograms = d.mosquePrograms.length + d.womenPrograms.length;
  const preachersCount = d.users.filter((u) => (Array.isArray(u.roles) && u.roles.length ? u.roles : [u.role]).includes("preacher")).length;

  // ── آخر النشاطات: مشتقّة من البيانات الحيّة المحمّلة (لا بيانات ثابتة) ──
  const recentActivities = (() => {
    const items = [];
    d.newMuslims.forEach((m) => items.push({
      type: "success",
      text: `مسلم جديد: ${m.name || "—"}`,
      date: m.created_at,
    }));
    d.events.forEach((e) => items.push({
      type: "info",
      text: `فعالية: ${e.title || "—"}`,
      date: e.created_at,
    }));
    [...d.donorIncome, ...d.finIncome].forEach((t) => items.push({
      type: "warning",
      text: `إيراد${t.amount ? ` بمبلغ ${Number(t.amount).toLocaleString("en-US")} ر.س` : ""}${t.description ? ` — ${t.description}` : ""}`,
      date: t.created_at,
    }));
    return items
      .filter((x) => x.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5)
      .map((x) => ({ ...x, time: formatActivityTime(x.date) }));
  })();

  const deptData = [
    { name: "الجاليات", value: d.newMuslims.length },
    { name: "المساجد", value: d.mosques.length },
    { name: "النسائي", value: d.womenBen.length },
    { name: "التطوع", value: d.volunteers.length },
    { name: "الأحداث", value: d.events.length },
  ];
  const totalBeneficiaries = deptData.reduce((t, x) => t + x.value, 0);

  // الإيرادات/المصروفات الشهرية: تجميع المعاملات الحيّة حسب شهر txn_date.
  const financeMap = new Map();
  const addFin = (rows, field) => rows.forEach((r) => {
    const b = monthBucket(r.txn_date);
    if (!b) return;
    const cur = financeMap.get(b.key) || { month: b.name, ts: b.ts, income: 0, expense: 0 };
    cur[field] += Number(r.amount) || 0;
    financeMap.set(b.key, cur);
  });
  addFin(d.finIncome, "income");
  addFin(d.donorIncome, "income");
  addFin(d.finExpense, "expense");
  const monthlyFinance = [...financeMap.values()].sort((a, b) => a.ts - b.ts)
    .map(({ month, income, expense }) => ({ month, income, expense }));

  // نمو المستفيدين الشهري: تراكمي للمسلمين الجدد حسب شهر islam_date.
  const growthMap = new Map();
  d.newMuslims.forEach((r) => {
    const b = monthBucket(r.islam_date);
    if (!b) return;
    const cur = growthMap.get(b.key) || { month: b.name, ts: b.ts, count: 0 };
    cur.count += 1;
    growthMap.set(b.key, cur);
  });
  let runningTotal = 0;
  const growthData = [...growthMap.values()].sort((a, b) => a.ts - b.ts)
    .map(({ month, count }) => ({ month, count: (runningTotal += count) }));

  // الأحداث القادمة الحيّة (من جدول events).
  const nowTs = new Date().getTime();
  const upcomingEvents = d.events
    .map((r) => ({ ...r, _d: new Date(r.event_date) }))
    .filter((e) => !isNaN(e._d.getTime()) && e._d.getTime() >= nowTs)
    .sort((a, b) => a._d - b._d)
    .slice(0, 4)
    .map((e) => ({
      title: e.title || "",
      day: String(e._d.getDate()),
      monthName: AR_MONTHS[e._d.getMonth()],
      location: e.location || "—",
      time: e.time || "",
      type: e.type || "",
      color: e.color || C.teal,
    }));

  // ملخص الأقسام (للمشرف) — إحصاءات حيّة.
  const money = (n) => n.toLocaleString("en-US");
  const deptSummary = [
    { name: "الجاليات", icon: Globe, stat: `${d.newMuslims.length} مسلم جديد`, color: "#2563eb" },
    { name: "المساجد", icon: Landmark, stat: `${d.mosques.length} مسجد • ${preachersCount} داعية`, color: "#0891b2" },
    { name: "النسائي", icon: Heart, stat: `${d.womenBen.length} مستفيدة • ${d.womenPrograms.length} برنامج`, color: "#db2777" },
    { name: "المالية", icon: Wallet, stat: `${money(totalRevenue)} إيراد • ${money(totalExpense)} مصروف`, color: "#16a34a" },
    { name: "الموارد البشرية", icon: Briefcase, stat: `${d.employees.length} موظف`, color: "#0284c7" },
    { name: "التطوع", icon: HandHelping, stat: `${d.volunteers.length} متطوع`, color: "#059669" },
  ];

  return (
    <div style={{ direction: "rtl" }}>
      <style>{`
        @media (max-width: 768px) {
          .dash-charts-row { grid-template-columns: 1fr !important; }
          .dash-welcome { flex-direction: column !important; gap: 12px !important; }
          .dash-quote { max-width: 100% !important; }
          .dash-dept-grid { grid-template-columns: 1fr !important; }
          .dash-actions-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .recharts-responsive-container { margin: 0 auto; }
        }
      `}</style>
      {/* ─── Welcome banner ─── */}
      <div style={styles.welcomeBanner}>
        <div className="dash-welcome" style={styles.welcomeContent}>
          <div>
            <h2 style={styles.welcomeTitle}>
              أهلاً، {user.name}
            </h2>
            <p style={styles.welcomeSubtitle}>
              {ROLE_NAMES[getPrimaryRole(user)]} — {ORG.shortName}
            </p>
          </div>
          <div style={styles.quoteCard}>
            <p style={styles.quoteText}>"{quote.text}"</p>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, margin: "4px 0 0" }}>— {quote.source}</p>
          </div>
        </div>
        <div style={styles.welcomeDeco} />
      </div>

      {/* ─── KPI Grid ─── */}
      <div style={styles.kpiGrid}>
        <KPICard title="إجمالي المستفيدين" value={totalBeneficiaries} icon={Users2} color={C.teal} />
        <KPICard title="البرامج النشطة" value={activePrograms} icon={Target} color="#7c3aed" />
        <KPICard title="الإيرادات (ريال)" value={totalRevenue} icon={DollarSign} color={C.success} />
        <KPICard title="المصروفات (ريال)" value={totalExpense} icon={Wallet} color={C.warning} />
        <KPICard title="المتطوعون" value={d.volunteers.length} icon={HandHelping} color="#059669" />
        <KPICard title="الدعاة" value={preachersCount} icon={Mic} color="#2563eb" />
        <KPICard title="المساجد" value={d.mosques.length} icon={Landmark} color="#0891b2" />
        <KPICard title="الأحداث القادمة" value={upcomingEvents.length} icon={CalendarDays} color="#ea580c" />
      </div>

      {/* ─── Charts Row ─── */}
      <div className="dash-charts-row" style={styles.chartsRow}>
        {/* Monthly Revenue vs Expenses */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>الإيرادات والمصروفات الشهرية</span>
            </div>
            <span style={styles.chartYear}>شهرياً</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthlyFinance} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v.toLocaleString()} ريال`]}
              />
              <Bar dataKey="income" name="الإيرادات" fill={C.teal} radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="المصروفات" fill={C.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Beneficiaries by Department */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>المستفيدون حسب القسم</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={deptData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value" paddingAngle={3}>
                {deptData.map((entry, i) => (
                  <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v.toLocaleString()} مستفيد`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {deptData.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: DEPT_COLORS[i] }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ─── Growth Trend + Quick Actions ─── */}
      <div className="dash-charts-row" style={styles.chartsRow}>
        {/* Monthly Growth */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Activity size={18} color={C.success} />
              <span style={styles.chartTitle}>نمو المستفيدين الشهري</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={growthData}>
              <defs>
                <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.teal} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={C.teal} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }} />
              <Area type="monotone" dataKey="count" stroke={C.teal} fill="url(#growthGrad)" strokeWidth={2.5} name="مستفيد" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Quick Actions */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Star size={18} color={C.accent} />
              <span style={styles.chartTitle}>إجراءات سريعة</span>
            </div>
          </div>
          <div className="dash-actions-grid" style={styles.quickGrid}>
            {QUICK_ACTIONS.filter(a => !a.adminOnly || isAdmin).map((action, i) => (
              <button
                key={i}
                onClick={() => onNavigate && onNavigate(action.module)}
                style={styles.quickBtn}
                title={action.label}
              >
                <div style={{ ...styles.quickIcon, background: `${action.color}15` }}>
                  <action.icon size={20} color={action.color} />
                </div>
                <span style={styles.quickLabel}>{action.label}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* ─── Recent Activity + Upcoming Events ─── */}
      <div className="dash-charts-row" style={styles.chartsRow}>
        {/* Recent Activity */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Clock size={18} color={C.primary} />
              <span style={styles.chartTitle}>آخر النشاطات</span>
            </div>
          </div>
          <div style={styles.activityList}>
            {recentActivities.length === 0 && (
              <p style={styles.emptyHint}>لا توجد نشاطات حديثة</p>
            )}
            {recentActivities.map((act, i) => (
              <div key={i} style={styles.activityItem}>
                <div style={{
                  ...styles.activityDot,
                  background: act.type === "success" ? C.success : act.type === "warning" ? C.warning : act.type === "info" ? C.teal : C.primary,
                }} />
                <div style={{ flex: 1 }}>
                  <p style={styles.activityText}>{act.text}</p>
                  <span style={styles.activityTime}>{act.time}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming Events */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CalendarDays size={18} color="#ea580c" />
              <span style={styles.chartTitle}>الأحداث القادمة</span>
            </div>
          </div>
          <div style={styles.eventsList}>
            {upcomingEvents.map((ev, i) => (
              <div key={i} style={styles.eventItem}>
                <div style={{ ...styles.eventDate, borderColor: ev.color }}>
                  <span style={styles.eventDay}>{ev.day}</span>
                  <span style={styles.eventMonth}>{ev.monthName}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={styles.eventTitle}>{ev.title}</p>
                  <p style={styles.eventDetail}>
                    <MapPin size={12} /> {ev.location} — {ev.time}
                  </p>
                </div>
                <div style={{ ...styles.eventTypeBadge, background: `${ev.color}15`, color: ev.color }}>
                  {ev.type}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ─── Department Summary (Admin only) ─── */}
      {isAdmin && (
        <Card style={{ marginTop: 20, padding: 24 }}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <LayoutDashboard size={18} color={C.primary} />
              <span style={styles.chartTitle}>ملخص الأقسام</span>
            </div>
          </div>
          <div className="dash-dept-grid" style={styles.deptGrid}>
            {deptSummary.map((dept, i) => (
              <div key={i} style={styles.deptCard}>
                <div style={{ ...styles.deptIcon, background: `${dept.color}15` }}>
                  <dept.icon size={22} color={dept.color} />
                </div>
                <div>
                  <p style={styles.deptName}>{dept.name}</p>
                  <p style={styles.deptStat}>{dept.stat}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   Preacher Dashboard — بيانات الداعية فقط
   ═════════════════════════════════════════════ */
/* ═════════════════════════════════════════════
   ✦ Institute Dashboard — لوحة مدير معهد الثمر الداني
   كل الأرقام حيّة من Supabase — لا بيانات ثابتة
   ═════════════════════════════════════════════ */
function InstituteDashboard({ user, quote }) {
  const [inst, setInst] = useState({ students: [], courses: [], attendance: [], certificates: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      getInstituteStudents(), getInstituteCourses(),
      getInstituteAttendance(), getInstituteCertificates(),
    ]).then(([st, co, at, ce]) => {
      if (!alive) return;
      const val = r => (r.status === "fulfilled" && Array.isArray(r.value) ? r.value : []);
      setInst({ students: val(st), courses: val(co), attendance: val(at), certificates: val(ce) });
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const activeStudents = inst.students.filter(s => s.academic_status !== "graduated" && s.academic_status !== "absent").length;
  const activeCourses = inst.courses.length;
  const certCount = inst.certificates.length;

  // نسبة الحضور = الجلسات الحاضرة ÷ كل الجلسات المسجّلة
  const attendanceRate = (() => {
    let present = 0, total = 0;
    inst.attendance.forEach(a => {
      (Array.isArray(a.sessions) ? a.sessions : []).forEach(v => {
        total++;
        if (v === "present") present++;
      });
    });
    return total ? Math.round((present / total) * 100) : 0;
  })();

  // الحضور الأسبوعي — جلسة لكل عمود عبر كل الطلاب
  const weeklyAttendance = (() => {
    const maxLen = inst.attendance.reduce((m, a) => Math.max(m, (a.sessions || []).length), 0);
    return Array.from({ length: maxLen }, (_, i) => {
      const col = inst.attendance.map(a => (a.sessions || [])[i]).filter(Boolean);
      return {
        session: `جلسة ${i + 1}`,
        حاضر: col.filter(v => v === "present").length,
        غائب: col.filter(v => v === "absent").length,
      };
    });
  })();

  const recentStudents = [...inst.students]
    .sort((a, b) => String(b.join_date || "").localeCompare(String(a.join_date || "")))
    .slice(0, 6)
    .map(s => ({
      name: s.name || "—",
      nationality: s.nationality || "—",
      level: s.current_level || "—",
      joinDate: s.join_date || "—",
    }));

  return (
    <div>
      <div style={styles.welcomeBanner}>
        <div className="dash-welcome" style={styles.welcomeContent}>
          <div>
            <h2 style={styles.welcomeTitle}>أهلاً، {user.name}</h2>
            <p style={styles.welcomeSubtitle}>{ROLE_NAMES[getPrimaryRole(user)]}</p>
          </div>
          <div style={styles.quoteCard}>
            <p style={styles.quoteText}>"{quote.text}"</p>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, margin: "4px 0 0" }}>— {quote.source}</p>
          </div>
        </div>
        <div style={styles.welcomeDeco} />
      </div>

      <div style={styles.kpiGrid}>
        <KPICard title="الطلاب النشطون" value={loading ? "..." : activeStudents} icon={Users} color="#2563eb" />
        <KPICard title="الدورات والمقررات" value={loading ? "..." : activeCourses} icon={BookOpen} color={C.teal} />
        <KPICard title="نسبة الحضور" value={loading ? "..." : `${attendanceRate}%`} icon={Activity} color={C.success} />
        <KPICard title="الشهادات الصادرة" value={loading ? "..." : certCount} icon={Award} color={C.accent} />
      </div>

      <Card style={{ marginTop: 20, padding: 20 }}>
        <div style={styles.chartHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <BarChart3 size={18} color={C.teal} />
            <span style={styles.chartTitle}>الحضور الأسبوعي</span>
          </div>
        </div>
        {weeklyAttendance.length === 0 ? (
          <p style={styles.emptyHint}>لا توجد سجلات حضور بعد</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={weeklyAttendance}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="session" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }} />
              <Bar dataKey="حاضر" fill={C.success} radius={[6, 6, 0, 0]} />
              <Bar dataKey="غائب" fill={C.danger} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card style={{ marginTop: 20, padding: 20 }}>
        <div style={styles.chartHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <GraduationCap size={18} color={C.accent} />
            <span style={styles.chartTitle}>آخر الطلاب المسجّلين</span>
          </div>
        </div>
        {recentStudents.length === 0 ? (
          <p style={styles.emptyHint}>لا يوجد طلاب مسجّلون بعد</p>
        ) : (
          <DataTable
            columns={[
              { key: "name", label: "الاسم" },
              { key: "nationality", label: "الجنسية" },
              { key: "level", label: "المستوى" },
              { key: "joinDate", label: "تاريخ الانضمام", nowrap: true },
            ]}
            data={recentStudents}
          />
        )}
      </Card>
    </div>
  );
}

function PreacherDashboard({ user, quote }) {
  // المهام المسندة للداعية — حيّة من إشعارات Supabase (type=task)
  const [preacherTasks, setPreacherTasks] = useState([]);
  useEffect(() => {
    let alive = true;
    const notifyId = authIdOf(user);
    getNotifications(notifyId)
      .then((rows) => {
        if (!alive) return;
        const tasks = (Array.isArray(rows) ? rows : [])
          .filter((n) => n.type === "task")
          .slice(0, 5)
          .map((n) => ({
            color: n.is_read ? C.success : C.warning,
            title: n.title,
            due: formatActivityTime(n.created_at) || "—",
            priority: n.is_read ? "مقروءة" : "جديدة",
          }));
        setPreacherTasks(tasks);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  const newMuslimsColumns = [
    { key: "name", label: "الاسم" },
    { key: "nationality", label: "الجنسية" },
    { key: "islamDate", label: "تاريخ الإسلام" },
    {
      key: "followUp",
      label: "حالة المتابعة",
      render: (row) => {
        const color =
          row.followUp === "منتظم" ? C.success :
          row.followUp === "متعثر" ? C.danger : C.warning;
        return (
          <span style={{
            padding: "3px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
            background: `${color}15`, color,
          }}>
            {row.followUp}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ direction: "rtl" }}>
      <style>{`
        @media (max-width: 768px) {
          .dash-charts-row { grid-template-columns: 1fr !important; }
          .dash-welcome { flex-direction: column !important; gap: 12px !important; }
          .dash-quote { max-width: 100% !important; }
        }
      `}</style>

      {/* ─── Welcome banner ─── */}
      <div style={styles.welcomeBanner}>
        <div className="dash-welcome" style={styles.welcomeContent}>
          <div>
            <h2 style={styles.welcomeTitle}>أهلاً، {user.name}</h2>
            <p style={styles.welcomeSubtitle}>{ROLE_NAMES[getPrimaryRole(user)]}</p>
          </div>
          <div style={styles.quoteCard}>
            <p style={styles.quoteText}>"{quote.text}"</p>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, margin: "4px 0 0" }}>— {quote.source}</p>
          </div>
        </div>
        <div style={styles.welcomeDeco} />
      </div>

      {/* ─── KPI Grid (preacher only) ─── */}
      <div style={styles.kpiGrid}>
        <KPICard title="برامجي المكلّف بها" value="0" icon={BookOpen} color="#2563eb" />
        <KPICard title="مستفيديّ" value="0" icon={Users} color={C.teal} />
        <KPICard title="مسلميّ الجدد" value="0" icon={Star} color={C.accent} />
        <KPICard title="تقاريري المطلوبة" value="0" icon={FileText} color={C.primary} />
        <KPICard title="مهامي المفتوحة" value="0" icon={ClipboardList} color={C.warning} />
      </div>

      {/* ─── Tasks + Meetings ─── */}
      <div className="dash-charts-row" style={styles.chartsRow}>
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ClipboardList size={18} color={C.primary} />
              <span style={styles.chartTitle}>المهام المسندة إليّ</span>
            </div>
          </div>
          <div style={styles.eventsList}>
            {preacherTasks.length === 0 && (
              <p style={styles.emptyHint}>لا توجد مهام مسندة إليك</p>
            )}
            {preacherTasks.map((t, i) => (
              <div key={i} style={styles.eventItem}>
                <div style={{ ...styles.eventDate, borderColor: t.color }}>
                  <CheckCircle size={18} color={t.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={styles.eventTitle}>{t.title}</p>
                  <p style={styles.eventDetail}>
                    <Clock size={12} /> الاستحقاق: {t.due}
                  </p>
                </div>
                <div style={{ ...styles.eventTypeBadge, background: `${t.color}15`, color: t.color }}>
                  {t.priority}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CalendarDays size={18} color={C.teal} />
              <span style={styles.chartTitle}>الاجتماعات القادمة</span>
            </div>
          </div>
          <div style={styles.eventsList}>
            {PREACHER_MEETINGS.length === 0 && (
              <p style={styles.emptyHint}>لا توجد اجتماعات قادمة</p>
            )}
            {PREACHER_MEETINGS.map((m, i) => (
              <div key={i} style={styles.eventItem}>
                <div style={{ ...styles.eventDate, borderColor: C.teal }}>
                  <span style={styles.eventDay}>{m.day}</span>
                  <span style={styles.eventMonth}>{m.monthName}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={styles.eventTitle}>{m.title}</p>
                  <p style={styles.eventDetail}>
                    <MapPin size={12} /> {m.location} — {m.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ─── Recent new muslims (preacher's own) ─── */}
      <Card style={{ marginTop: 20, padding: 20 }}>
        <div style={styles.chartHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Star size={18} color={C.accent} />
            <span style={styles.chartTitle}>آخر المسلمين الجدد المسجّلين عندي</span>
          </div>
        </div>
        <DataTable columns={newMuslimsColumns} data={PREACHER_NEW_MUSLIMS} />
      </Card>

      {/* ─── Monthly conversions chart (preacher's own) ─── */}
      <Card style={{ marginTop: 20, padding: 20 }}>
        <div style={styles.chartHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <BarChart3 size={18} color={C.teal} />
            <span style={styles.chartTitle}>مسلميّ الجدد شهرياً</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={PREACHER_MONTHLY_CONVERSIONS}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
            <YAxis fontSize={11} tick={{ fill: C.textLight }} allowDecimals={false} />
            <Tooltip
              cursor={{ fill: `${C.teal}10` }}
              contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
              formatter={(v) => [`${v}`, "مسلم جديد"]}
            />
            <Bar dataKey="count" name="مسلم جديد" fill={C.teal} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Helpers + Static UI Data
   ═════════════════════════════════════════════ */
// أسماء الأشهر الميلادية (0-based) — تُستخدم لتجميع المعاملات/المسلمين الجدد حسب الشهر.
const AR_MONTHS = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];
// يحوّل تاريخ ISO إلى مفتاح شهر (سنة-شهر) + اسم عربي + ترتيب زمني؛ null إن كان التاريخ غير صالح.
function monthBucket(dateStr) {
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return null;
  return {
    key: `${dt.getFullYear()}-${dt.getMonth()}`,
    name: AR_MONTHS[dt.getMonth()],
    ts: dt.getFullYear() * 12 + dt.getMonth(),
  };
}

// يعرض تاريخ نشاط بصيغة "اليوم اسم-الشهر" ميلاديًا (يعيد استخدام monthBucket/AR_MONTHS).
function formatActivityTime(dateStr) {
  const b = monthBucket(dateStr);
  if (!b) return "";
  return `${new Date(dateStr).getDate()} ${b.name}`;
}

const DEPT_COLORS = ["#2563eb", "#0891b2", "#db2777", "#059669", "#ea580c"];

const QUICK_ACTIONS = [
  { label: "إضافة متبرع", icon: HeartHandshake, color: "#d97706", module: "donors" },
  { label: "تسجيل مصروف", icon: Wallet, color: "#16a34a", module: "finance" },
  { label: "إضافة متطوع", icon: HandHelping, color: "#059669", module: "volunteer" },
  { label: "تقرير مالي", icon: FileText, color: C.primary, module: "finance", adminOnly: true },
  { label: "إدارة المستخدمين", icon: UserCog, color: "#475569", module: "users", adminOnly: true },
];

// PREACHER_MEETINGS: لا يوجد مصدر اجتماعات خاص بالداعية حتى الآن (جدول meetings مقسّم حسب module
// ولا يوجد module للداعية) — يبقى فارغًا بحالة فارغة نظيفة لحين تحديد المصدر.
const PREACHER_MEETINGS = [];

const PREACHER_NEW_MUSLIMS = [];
const PREACHER_MONTHLY_CONVERSIONS = [];
/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  /* Welcome */
  welcomeBanner: {
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    borderRadius: 16,
    padding: "28px 32px",
    marginBottom: 24,
    position: "relative",
    overflow: "hidden",
    color: C.white,
  },
  welcomeContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
    flexWrap: "wrap",
    gap: 16,
  },
  welcomeTitle: {
    fontSize: 22,
    fontWeight: 700,
    margin: "0 0 4px",
  },
  welcomeSubtitle: {
    fontSize: 14,
    opacity: 0.8,
    margin: 0,
  },
  quoteCard: {
    background: "rgba(255,255,255,0.12)",
    borderRadius: 10,
    padding: "12px 20px",
    maxWidth: 400,
    borderRight: `3px solid ${C.accent}`,
  },
  quoteText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.8,
    opacity: 0.95,
  },
  welcomeDeco: {
    position: "absolute",
    top: -40,
    left: -40,
    width: 200,
    height: 200,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
  },

  /* KPI */
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },

  /* Charts */
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
    gap: 20,
    marginBottom: 20,
  },
  chartCard: {
    padding: 20,
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  chartTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: C.primary,
  },
  chartYear: {
    fontSize: 12,
    color: C.textLight,
    background: C.bg,
    padding: "4px 10px",
    borderRadius: 6,
  },

  /* Legend */
  legendGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "center",
    marginTop: 8,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  /* Quick Actions */
  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
    gap: 10,
  },
  quickBtn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: 14,
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    cursor: "pointer",
    fontFamily: "'Readex Pro', sans-serif",
    transition: "all 0.2s",
  },
  quickIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  quickLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: C.primary,
    textAlign: "center",
  },

  /* Activity */
  activityList: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  emptyHint: {
    fontSize: 13,
    color: C.textLight,
    textAlign: "center",
    padding: "24px 0",
    margin: 0,
  },
  activityItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 0",
    borderBottom: `1px solid ${C.bg}`,
  },
  activityDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginTop: 4,
    flexShrink: 0,
  },
  activityText: {
    margin: 0,
    fontSize: 13,
    color: C.primary,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  activityTime: {
    fontSize: 11,
    color: C.textLight,
  },

  /* Events */
  eventsList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  eventItem: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    padding: 12,
    background: C.bg,
    borderRadius: 10,
  },
  eventDate: {
    width: 52,
    height: 52,
    borderRadius: 10,
    background: C.white,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid",
    flexShrink: 0,
  },
  eventDay: {
    fontSize: 18,
    fontWeight: 700,
    color: C.primary,
    lineHeight: 1,
  },
  eventMonth: {
    fontSize: 10,
    color: C.textLight,
    marginTop: 2,
  },
  eventTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: C.primary,
  },
  eventDetail: {
    margin: "4px 0 0",
    fontSize: 12,
    color: C.textLight,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  eventTypeBadge: {
    padding: "4px 10px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  /* Department Summary */
  deptGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 20,
    marginTop: 12,
  },
  deptCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 14,
    background: C.bg,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
  },
  deptIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  deptName: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: C.primary,
  },
  deptStat: {
    margin: "2px 0 0",
    fontSize: 12,
    color: C.textLight,
  },
  deptTrend: {
    fontSize: 13,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 2,
    marginRight: "auto",
  },
};
